import { useEffect } from 'react'
import confetti from 'canvas-confetti'
import { AnimatePresence, motion } from 'framer-motion'
import type { ExperienceReveal, PendingCelebration } from '../../types/experienceEngine'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useNls } from '../../nls/nlsProvider'
import './ExperienceCelebrationOverlay.css'

interface Props {
  reveal: ExperienceReveal | null
  onComplete: (items: PendingCelebration[]) => Promise<void>
}

const playTone = (frequency: number | undefined) => {
  if (!frequency) return
  const context = new AudioContext()
  const oscillator = context.createOscillator()
  const gain = context.createGain()
  oscillator.frequency.value = frequency
  gain.gain.setValueAtTime(0.0001, context.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.03)
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.45)
  oscillator.connect(gain).connect(context.destination)
  oscillator.start()
  oscillator.stop(context.currentTime + 0.5)
  oscillator.addEventListener('ended', () => void context.close())
}

export const ExperienceCelebrationOverlay = ({ reveal, onComplete }: Props) => {
  const { t } = useNls()
  const { preferences } = useClientPreferences()
  useEffect(() => {
    if (!reveal) return undefined
    const tier = reveal.registry.performance.tiers[reveal.performanceTier]
    const effect = reveal.registry.celebrationCatalog[reveal.items[0]?.effect]
    if (preferences.enableCelebration && tier.renderer === 'CANVAS_CONFETTI') {
      void confetti({
        particleCount: tier.particleCount,
        spread: tier.spread,
        gravity: tier.gravity,
        decay: tier.decay,
        origin: { y: 0.28 },
        disableForReducedMotion: true,
      })
    }
    try {
      playTone(effect?.soundFrequencyHz)
    } catch {
      // Browsers can reject non-gesture audio; the visual remains authoritative.
    }
    const timer = window.setTimeout(() => void onComplete(reveal.items), tier.durationMs)
    return () => window.clearTimeout(timer)
  }, [onComplete, preferences.enableCelebration, reveal])

  const transitionSeconds = (reveal?.registry.engine.timing.overlayTransitionMs ?? 300) / 1000
  return (
    <AnimatePresence>
      {reveal && (
        <motion.div
          className={`experienceReveal experienceReveal--${reveal.performanceTier.toLowerCase()}`}
          initial={{ opacity: 0, y: -18, x: '-50%', scale: 0.96 }}
          animate={{ opacity: 1, y: 0, x: '-50%', scale: 1 }}
          exit={{ opacity: 0, y: -12, x: '-50%', scale: 0.98 }}
          transition={{ duration: transitionSeconds, ease: 'easeOut' }}
          role="status"
          aria-live="polite"
        >
          <div className="experienceReveal__eyebrow">{t('NLSExperienceAchievementUnlocked')}</div>
          <h2>{reveal.items.length === 1 ? reveal.items[0].title : t('NLSExperienceAchievementsCollected', { count: reveal.items.length })}</h2>
          <ul>
            {reveal.items.map((item) => <li key={item.id}>{item.title}</li>)}
          </ul>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
